import React, { useEffect, useState } from "react";
import { ArrowLeft, Eye, RotateCcw } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ServiceLifecycleDialog from "../components/service/ServiceLifecycleDialog";
import {
  ServiceBranchSearchRow,
  ServiceListPagination,
} from "../components/service/ServiceListControls";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { listServiceTickets, restoreServiceTicket } from "../lib/serviceApi";
import {
  SERVICE_BRANCHES,
  SERVICE_BRANCH_LABELS,
  SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  SERVICE_STAGE_LABELS,
} from "../lib/serviceConstants";
import {
  canRestoreServiceTicket,
  canViewDeletedServiceTickets,
} from "../lib/servicePermissions";
import { Button } from "../components/ui/button";
import {
  LIST_PAGE_SIZE_OPTIONS,
  ListErrorNotice,
  ListTable,
  ListTableShell,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_BRANCH = SERVICE_BRANCHES.JAKARTA;
const SEARCH_DEBOUNCE_MS = 400;
function normalizeBranch(value) {
  return Object.values(SERVICE_BRANCHES).includes(value) ? value : DEFAULT_BRANCH;
}

function normalizePage(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

function normalizePageSize(value) {
  const parsed = Number.parseInt(value, 10);
  return LIST_PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : DEFAULT_PAGE_SIZE;
}

export function buildDeletedListSearchParams({ branch, search, page, pageSize }) {
  const params = new URLSearchParams();
  params.set("branch", normalizeBranch(branch));
  params.set("page", String(normalizePage(page)));
  const normalizedPageSize = normalizePageSize(pageSize);
  if (normalizedPageSize !== DEFAULT_PAGE_SIZE) params.set("page_size", String(normalizedPageSize));
  const trimmedSearch = String(search || "").trim();
  if (trimmedSearch) params.set("search", trimmedSearch);
  return params;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export default function ServiceDeletedTickets({ category }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedBranch = normalizeBranch(searchParams.get("branch"));
  const page = normalizePage(searchParams.get("page"));
  const pageSize = normalizePageSize(searchParams.get("page_size"));
  const appliedSearch = (searchParams.get("search") || "").trim();
  const [searchInput, setSearchInput] = useState(appliedSearch);
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [restoreTicket, setRestoreTicket] = useState(null);
  const [restoreError, setRestoreError] = useState("");
  const isKnownCategory = Object.values(SERVICE_CATEGORIES).includes(category);
  const canViewDeleted = canViewDeletedServiceTickets(user?.role);

  useEffect(() => {
    if (!isKnownCategory || !canViewDeleted) return undefined;
    const timeout = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== appliedSearch) {
        setSearchParams(
          buildDeletedListSearchParams({
            branch: selectedBranch,
            search: trimmed,
            page: 1,
            pageSize,
          }),
          { replace: true }
        );
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [appliedSearch, canViewDeleted, isKnownCategory, pageSize, searchInput, selectedBranch, setSearchParams]);

  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  useEffect(() => {
    if (!isKnownCategory || !canViewDeleted) return;
    const next = buildDeletedListSearchParams({
      branch: selectedBranch,
      search: appliedSearch,
      page,
      pageSize,
    });
    if (searchParams.toString() !== next.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [appliedSearch, canViewDeleted, isKnownCategory, page, pageSize, searchParams, selectedBranch, setSearchParams]);

  useEffect(() => {
    if (!isKnownCategory || !canViewDeleted) return undefined;
    let cancelled = false;

    async function loadDeletedTickets() {
      setLoading(true);
      setError("");
      try {
        const params = {
          category,
          branch: selectedBranch,
          is_deleted: true,
          page,
          page_size: pageSize,
          sort_by: "deleted_at",
          sort_order: "desc",
        };
        if (appliedSearch) params.search = appliedSearch;
        const result = await listServiceTickets(params);
        if (cancelled) return;
        const resultTotalPages = result?.pagination?.total_pages ?? 0;
        const lastPage = Math.max(resultTotalPages, 1);
        if (page > lastPage) {
          setSearchParams(
            buildDeletedListSearchParams({ branch: selectedBranch, search: appliedSearch, page: lastPage, pageSize }),
            { replace: true }
          );
          return;
        }
        setTickets(Array.isArray(result?.data) ? result.data : []);
        setPagination({
          page: result?.pagination?.page ?? page,
          page_size: result?.pagination?.page_size ?? pageSize,
          total: result?.pagination?.total ?? 0,
          total_pages: result?.pagination?.total_pages ?? 0,
        });
      } catch (err) {
        if (cancelled) return;
        setTickets([]);
        setPagination({ page, page_size: pageSize, total: 0, total_pages: 0 });
        setError(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDeletedTickets();
    return () => {
      cancelled = true;
    };
  }, [appliedSearch, canViewDeleted, category, isKnownCategory, page, pageSize, refreshKey, selectedBranch, setSearchParams]);

  if (!isKnownCategory || !canViewDeleted) return null;

  const categoryLabel = SERVICE_CATEGORY_LABELS[category];
  const currentDeletedReturnUrl = () => {
    const params = buildDeletedListSearchParams({
      branch: selectedBranch,
      search: appliedSearch,
      page,
      pageSize,
    });
    return `/service/${category}/deleted?${params.toString()}`;
  };
  const activeListRoute = () => {
    const params = new URLSearchParams();
    params.set("branch", selectedBranch);
    params.set("stage", "incoming");
    params.set("page", "1");
    if (appliedSearch) params.set("search", appliedSearch);
    return `/service/${category}?${params.toString()}`;
  };
  const detailRoute = (ticket) => {
    const returnUrl = encodeURIComponent(currentDeletedReturnUrl());
    return `/service/tickets/${encodeURIComponent(ticket.id)}?return=${returnUrl}`;
  };
  const changeBranch = (branch) => {
    if (branch === selectedBranch) return;
    setSearchParams(
      buildDeletedListSearchParams({ branch, search: appliedSearch, page: 1, pageSize }),
      { replace: true }
    );
  };
  const closeRestore = () => {
    setRestoreTicket(null);
    setRestoreError("");
  };
  const confirmRestore = async (reason) => {
    if (!restoreTicket) return;
    setRestoreError("");
    try {
      await restoreServiceTicket(restoreTicket.id, { reason });
      const ticketNumber = restoreTicket.ticket_number;
      closeRestore();
      setNotice(
        ticketNumber
          ? `Service ticket ${ticketNumber} restored successfully.`
          : "Service ticket restored successfully."
      );
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setRestoreError(formatApiError(err));
      throw err;
    }
  };

  return (
    <OrdinaryPageBody data-testid={`service-deleted-tickets-${category}`}>
      <OrdinaryPageHeader
        title={`Deleted ${categoryLabel} Tickets`}
        actions={<Button
          type="button"
          onClick={() => navigate(activeListRoute())}
          variant="navigation-back"
          data-action-category="navigation-back"
          data-testid="service-back-to-active"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Active Tickets
        </Button>}
        data-testid="service-deleted-list-header"
      />

      <ListTableShell data-testid="service-deleted-list-panel">
        <ServiceBranchSearchRow
          selectedBranch={selectedBranch}
          onBranchChange={changeBranch}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          testIdPrefix="service-deleted"
        />

        {error ? <ListErrorNotice testId="service-deleted-list-error">{error}</ListErrorNotice> : null}
        {notice ? <div className="px-6 py-4 text-sm font-semibold text-success" data-testid="service-restore-success">{notice}</div> : null}

        <ListTableViewport>
          <ListTable className="min-w-[1500px]" data-testid="service-deleted-ticket-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Deleted At</th>
                <th>Ticket Number</th>
                <th>Customer</th>
                <th>Serial Number</th>
                <th>Model</th>
                <th>Branch</th>
                <th>Last Stage</th>
                <th>Deleted By</th>
                <th>Delete Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <ListTableState colSpan={11} kind="loading">Loading deleted tickets…</ListTableState>
              ) : tickets.length === 0 ? (
                <ListTableState colSpan={11}>No deleted {categoryLabel.toLowerCase()} Service tickets found.</ListTableState>
              ) : (
                tickets.map((ticket, index) => (
                  <tr key={ticket.id} data-testid={`service-deleted-row-${ticket.id}`}>
                    <td className="num">{(pagination.page - 1) * pagination.page_size + index + 1}</td>
                    <td className="text-xs font-medium tabular-nums">{formatDateTime(ticket.deleted_at)}</td>
                    <td className="text-xs font-semibold tracking-[0.02em]">{ticket.ticket_number || "-"}</td>
                    <td className="text-xs">{ticket.owner?.name || "-"}</td>
                    <td className="text-xs font-medium tracking-[0.02em]">{ticket.device?.serial_number || "-"}</td>
                    <td className="text-xs">{ticket.device?.model || "-"}</td>
                    <td className="text-xs">{SERVICE_BRANCH_LABELS[ticket.branch] || ticket.branch || "-"}</td>
                    <td className="text-xs">{SERVICE_STAGE_LABELS[ticket.stage] || ticket.stage || "-"}</td>
                    <td className="text-xs font-medium">{ticket.deleted_by || "-"}</td>
                    <td className="max-w-[260px] text-xs">{ticket.delete_reason || "-"}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          onClick={() => navigate(detailRoute(ticket))}
                          aria-label={`View deleted Service ticket ${ticket.ticket_number || ""}`.trim()}
                          variant="info"
                          size="row-icon"
                          title="View deleted ticket"
                          data-action-category="view-info"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canRestoreServiceTicket(user?.role) ? (
                          <Button
                            type="button"
                            onClick={() => {
                              setRestoreError("");
                              setRestoreTicket(ticket);
                            }}
                            aria-label={`Restore Service ticket ${ticket.ticket_number || ""}`.trim()}
                            variant="restore"
                            size="row-icon"
                            title="Restore ticket"
                            data-action-category="restore-recovery"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ListTable>
        </ListTableViewport>

        <ServiceListPagination
          page={pagination.page}
          pageSize={pageSize}
          total={pagination.total}
          totalPages={pagination.total_pages}
          loading={loading}
          testIdPrefix="service-deleted"
          onPrevious={() =>
            setSearchParams(
              buildDeletedListSearchParams({
                branch: selectedBranch,
                search: appliedSearch,
                page: Math.max(1, page - 1),
                pageSize,
              }),
              { replace: true }
            )
          }
          onNext={() =>
            setSearchParams(
              buildDeletedListSearchParams({
                branch: selectedBranch,
                search: appliedSearch,
                page: page + 1,
                pageSize,
              }),
              { replace: true }
            )
          }
          onPageSizeChange={(nextPageSize) =>
            setSearchParams(
              buildDeletedListSearchParams({ branch: selectedBranch, search: appliedSearch, page: 1, pageSize: nextPageSize }),
              { replace: true }
            )
          }
        />

      </ListTableShell>

      <ServiceLifecycleDialog
        key={restoreTicket ? `restore-${restoreTicket.id}` : "restore-closed"}
        mode="restore"
        open={Boolean(restoreTicket)}
        ticket={restoreTicket}
        error={restoreError}
        onClose={closeRestore}
        onConfirm={confirmRestore}
      />
    </OrdinaryPageBody>
  );
}
